import React, { ReactNode } from "react"
import { Control } from "react-hook-form"
import {
  Flex, FormControl, FormLabel, Stack, Text,
} from "@chakra-ui/react"
import { GuestStrategy as GuestStrategyType } from "sharedTypes/index"
import UpgradeAlert from "components/elements/UpgradeAlert"
import useCurrentPlanType from "utilities/useCurrentPlanType"
import ToggleButtonGroup, { ToggleButtonGroupItem } from "components/ReactHookForm/ToggleButtonGroup"

type Props = {
  control: Control
}

const cards = [
  {
    value: GuestStrategyType.Create,
    title: "Always create a new guest",
    description:
      "A new guest will be created for every form submission, even if the email matches an existing guest.",
  },
  {
    value: GuestStrategyType.Upsert,
    title: "Prevent creating duplicates",
    description:
      "If a guest with the same email already exists in this event, the registration will be saved under their existing profile.",
  },
  {
    value: GuestStrategyType.Restrict,
    title: "Restrict to existing guests",
    description:
      "Only guests already added to this event can register. If the email entered doesn’t match an existing guest, the form submission will be blocked.",
    corporateOnly: true,
  },
]

const GuestCreationCards = ({ control }: Props) => {
  const { corporateFeaturesEnabled } = useCurrentPlanType()
  const showUpgrade = cards.some((card) => card.corporateOnly) && !corporateFeaturesEnabled

  return (
    <FormControl mb={4}>
      <Flex justify="space-between" align="center" mb={3}>
        <FormLabel fontWeight="semibold" mb={0}>Guest Creation Settings</FormLabel>
        {showUpgrade && <UpgradeAlert noBorder mb={0} text="Requires Corporate Plan or higher" />}
      </Flex>
      <ToggleButtonGroup name="guestStrategy" control={control} columns={3} spacing={4}>
        {cards.map((card) => {
          const disabled = Boolean(card.corporateOnly) && !corporateFeaturesEnabled

          return {
            value: card.value,
            disabled,
            button: (selected: boolean, onClick: () => void) => (
              <ToggleButtonGroupItem
                selected={selected && !disabled}
                onClick={onClick}
                isDisabled={disabled}
                h="full"
                minH={24}
                p={4}
                whiteSpace="normal"
                alignItems="flex-start"
                textAlign="left"
              >
                <CardContent title={card.title} disabled={disabled}>
                  {card.description}
                </CardContent>
              </ToggleButtonGroupItem>
            ),
          }
        })}
      </ToggleButtonGroup>
    </FormControl>
  )
}

type CardContentProps = {
  title: string
  disabled: boolean
  children: ReactNode
}

const CardContent = ({ title, disabled, children }: CardContentProps) => (
  <Stack spacing={2} align="flex-start" opacity={disabled ? 0.5 : 1}>
    <Text fontSize="md" fontWeight="semibold">{title}</Text>
    <Text fontSize="sm" fontWeight="normal" color="gray.900">{children}</Text>
  </Stack>
)

export default GuestCreationCards
